import React, { useState } from 'react';
import { extractImages, isRenderableImage } from '../utils/imageMarkers';

// Shared rendering for clinical text fields (consultation notes/diagnosis, lab
// results, prescriptions, ...). Images are stored inline inside these fields as
// [IMAGE:data:image/...] markers. These components run every field through the
// single shared parser (imageMarkers.extractImages) so the readable text is
// always preserved and the embedded images are shown as actual images — the raw
// Base64 string is NEVER shown to the patient.

// Renders a single embedded image with graceful fallback if it cannot be displayed.
export function PatientImage({ image, maxHeight = 300 }) {
    const [failed, setFailed] = useState(!isRenderableImage(image.dataUrl));

    if (failed) {
        return (
            <div
                style={{
                    marginTop: '8px',
                    padding: '12px',
                    border: '2px dashed #e0a0a0',
                    borderRadius: '6px',
                    background: '#fdf2f2',
                    color: '#9a3b3b',
                    fontSize: '0.9rem'
                }}
            >
                <i className="fas fa-image"></i> {image.label}: image could not be displayed.
            </div>
        );
    }

    const openFullSize = () => {
        const win = window.open();
        if (!win) return;
        win.document.write(`
            <html>
                <head><title>${image.label}</title></head>
                <body style="margin:0;padding:20px;text-align:center;background:#f5f5f5;">
                    <h2 style="font-family:sans-serif;color:#2c3e50;">${image.label}</h2>
                    <img src="${image.dataUrl}" style="max-width:100%;height:auto;border-radius:8px;box-shadow:0 4px 8px rgba(0,0,0,0.1);" />
                </body>
            </html>
        `);
    };

    return (
        <div style={{ marginTop: '8px' }}>
            <img
                src={image.dataUrl}
                alt={image.label}
                onError={() => setFailed(true)}
                onClick={openFullSize}
                style={{
                    maxWidth: '100%',
                    maxHeight: `${maxHeight}px`,
                    borderRadius: '6px',
                    border: '2px solid #ddd',
                    objectFit: 'contain',
                    cursor: 'pointer',
                    display: 'block'
                }}
            />
            <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                <i className="fas fa-info-circle"></i> {image.label} — click to view full size
            </small>
        </div>
    );
}

// Renders a clinical text field: readable text with all embedded images shown as
// actual images. Raw Base64 markers are never shown.
export function ClinicalText({ text, maxHeight = 300 }) {
    const { cleanText, images } = extractImages(text);

    return (
        <>
            {cleanText &&
                cleanText.split('\n').map((line, index) =>
                    line.trim() ? (
                        <p key={`line-${index}`} style={{ marginBottom: '5px' }}>
                            {line}
                        </p>
                    ) : null
                )}
            {images.map((image, index) => (
                <PatientImage key={`img-${index}`} image={image} maxHeight={maxHeight} />
            ))}
        </>
    );
}

export default ClinicalText;
